import Modal from '../../common/Modal';
import CustomButton from '../../common/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { searchFacilitiesRequest } from '../../../api/requests/facility';
import {
  BlueCalendarIcon,
  ModalContent,
  StyledDatePicker,
  StyledFormItem,
  StyledInput,
  StyledTimePicker,
} from './SinglesBrackets';
import { Button, Form, Select } from 'antd';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { debounce } from '../../../utils/utils';
import { updateTournamentMatchDetailsRequest } from '../../../api/requests/tournaments';
import {
  FacilityType,
  TournamentMatchUpdatePayload,
} from '../../../types/types';
import { EnvironmentOutlined } from '@ant-design/icons';
import LocationPicker, { LocationValue } from '../Maps/LocationPicker';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import { FacilityStatus } from '../../../enums/enums';

interface MatchVenueDetailsModalProps {
  visible: boolean;
  handleClose: () => void;
  initialValues?: TournamentMatchUpdatePayload;
  onUpdate?: () => void;
}

const MatchVenueDetailsModal = ({
  visible,
  handleClose,
  initialValues,
  onUpdate,
}: MatchVenueDetailsModalProps) => {
  const [form] = Form.useForm();
  const [facilities, setFacilities] = useState<FacilityType[]>([]);
  const [isFacilitiesLoading, setIsFacilitiesLoading] =
    useState<boolean>(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState<boolean>(false);
  const [venueCoordinates, setVenueCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isInitialLoadComplete, setIsInitialLoadComplete] =
    useState<boolean>(false);
  const snackbar = useSnackbar();

  const searchFacilities = useCallback(async (text: string) => {
    try {
      setIsFacilitiesLoading(true);
      const response = await searchFacilitiesRequest({
        searchQuery: text,
        FacilityStatus: FacilityStatus.Approved,
      });
      if (response?.isSuccess) {
        setFacilities(response?.value?.rows || []);
      }
    } catch (error) {
      console.error('Error searching facilities:', error);
    } finally {
      setIsFacilitiesLoading(false);
    }
  }, []);

  const handleUpdateMatch = async () => {
    setHasSubmitted(true);
    try {
      const values = await form.validateFields();
      const date: Dayjs = values.date;
      const time: Dayjs = values.time;

      const payload: TournamentMatchUpdatePayload = {
        tournamentMatchId: initialValues?.tournamentMatchId || 0,
        tournamentMatchLocation: values.venue,
        scheduledStartTime: dayjs(
          `${date.format('YYYY-MM-DD')} ${time.format('HH:mm:ss')}`,
        )
          .toDate()
          .toISOString(),
        facilityId: values.facilityId,
      };

      const response = await updateTournamentMatchDetailsRequest(
        payload.tournamentMatchId,
        payload,
      );
      setIsSubmitting(true);
      if (response?.isSuccess) {
        snackbar.success('Match updated successfully.');
        onUpdate && onUpdate();
        handleClose();
      } else {
        snackbar.error(response?.error || 'Failed to update match.');
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFacilitySelection = (facilityId: number) => {
    const facility = facilities?.find((f) => f.facilityId === facilityId);
    if (facility) {
      const facilityDetails =
        facility.facilityAddress +
        ', ' +
        facility.city +
        ', ' +
        facility.state +
        ', ' +
        facility.zipCode;
      form.setFieldValue('venue', facilityDetails || '');
      // Store facility coordinates
      if (facility.facilityLatitude && facility.facilityLongitude) {
        setVenueCoordinates({
          lat: facility.facilityLatitude,
          lng: facility.facilityLongitude,
        });
      }
    }
  };

  const handleLocationChange = (location: LocationValue) => {
    const addressString = location.address
      ? `${location.address.street}, ${location.address.city}, ${location.address.state} ${location.address.pincode}`
      : '';
    form.setFieldValue('venue', addressString);
    setVenueCoordinates({
      lat: location.lat,
      lng: location.lng,
    });
    setLocationPickerOpen(false);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((text: string) => {
        searchFacilities(text);
      }, 500),
    [searchFacilities],
  );

  useEffect(() => {
    const initializeFacilities = async () => {
      setIsFacilitiesLoading(true);
      await searchFacilities('');
      setIsInitialLoadComplete(true);
      setIsFacilitiesLoading(false);
    };

    if (visible) {
      setIsInitialLoadComplete(false);
      initializeFacilities();
    }
  }, [searchFacilities, visible]);

  useEffect(() => {
    if (initialValues && isInitialLoadComplete) {
      // Check if the initial facility is in the list
      const initialFacility = facilities.find(
        (f) => f.facilityId === initialValues.facilityId,
      );

      // Set form values once initial load is complete
      form.setFieldsValue({
        facilityId: initialValues.facilityId,
        venue: initialValues.tournamentMatchLocation,
        date: initialValues.scheduledStartTime
          ? dayjs(initialValues.scheduledStartTime)
          : null,
        time: initialValues.scheduledStartTime
          ? dayjs(initialValues.scheduledStartTime)
          : null,
      });

      // Set venue coordinates if available from facilities
      if (initialFacility) {
        if (
          initialFacility.facilityLatitude &&
          initialFacility.facilityLongitude
        ) {
          setVenueCoordinates({
            lat: initialFacility.facilityLatitude,
            lng: initialFacility.facilityLongitude,
          });
        }
      }
    }
  }, [initialValues, form, facilities, isInitialLoadComplete]);

  return (
    <Modal
      title="Update Match Details"
      open={visible}
      onCancel={handleClose}
      width={500}
      minHeight={'auto'}
      footer={[
        <CustomButton key="cancel" onClick={handleClose}>
          Cancel
        </CustomButton>,
        <CustomButton
          key="save"
          type="primary"
          onClick={handleUpdateMatch}
          disabled={isSubmitting}
        >
          Update & Save
        </CustomButton>,
      ]}
    >
      <ModalContent>
        <Form
          form={form}
          layout="vertical"
          validateTrigger={hasSubmitted ? 'onChange' : 'onSubmit'}
        >
          <LabelWithLink>
            <FacilityItem>
              <StyledFormItem name="facilityId" label={'Facility'}>
                <StyledSelect
                  placeholder="Select facility"
                  size="middle"
                  loading={isFacilitiesLoading || !isInitialLoadComplete}
                  showSearch
                  optionFilterProp="children"
                  onSearch={(value) => debouncedSearch(value)}
                  onSelect={(value) => handleFacilitySelection(value as number)}
                  disabled={!isInitialLoadComplete}
                >
                  {facilities.map((facility) => (
                    <Select.Option
                      key={facility.facilityId}
                      value={facility.facilityId}
                    >
                      {facility.facilityName}
                    </Select.Option>
                  ))}
                </StyledSelect>
              </StyledFormItem>
            </FacilityItem>
            <LocationGroup>
              <Separator>/</Separator>
              <PickLocationLink
                type="primary"
                onClick={() => setLocationPickerOpen(true)}
              >
                <EnvironmentOutlined />
                Location
              </PickLocationLink>
            </LocationGroup>
          </LabelWithLink>
          <StyledFormItem
            name="venue"
            label="Venue"
            rules={[{ required: true, message: 'Please enter Venue' }]}
          >
            <StyledInput type="text" placeholder="Enter location" />
          </StyledFormItem>
          <StyledFormItem
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <StyledDatePicker
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              suffixIcon={<BlueCalendarIcon />}
            />
          </StyledFormItem>
          <StyledFormItem
            name="time"
            label="Time"
            rules={[{ required: true, message: 'Please select time' }]}
          >
            <StyledTimePicker format="hh:mm A" placeholder="hh:mm AM/PM" />
          </StyledFormItem>
        </Form>
      </ModalContent>

      <Modal
        title="Pick Match Venue Location"
        open={locationPickerOpen}
        onCancel={() => setLocationPickerOpen(false)}
        footer={null}
        width={1200}
      >
        <LocationPicker
          value={
            venueCoordinates
              ? {
                  lat: venueCoordinates.lat,
                  lng: venueCoordinates.lng,
                  address: undefined,
                }
              : undefined
          }
          onLocationChange={handleLocationChange}
          height="500px"
        />
      </Modal>
    </Modal>
  );
};

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    height: 43px !important;
  }
`;

const LabelWithLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FacilityItem = styled.div`
  flex: 1;
`;

const LocationGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
`;

const Separator = styled.span`
  margin: 0 4px;
  font-size: ${TEXT_SIZES.MD}px;
  color: ${COLORS.grey.primary};
`;

const PickLocationLink = styled(Button)`
  cursor: pointer;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  display: flex;
  gap: 4px;
  font-size: ${TEXT_SIZES.SM}px;
  height: 40px;
  border-radius: 8px;
`;

export default MatchVenueDetailsModal;
